<template>
<div class="pa-0 ma-0 fill-height" style="background-color:#f5f5f5" fluid>
    <v-dialog
          v-model="overlay"
          hide-overlay
          persistent
          width="300"
          lazy          
        >
          <v-card
            color="secondary"
            dark
          >
            <v-card-text>
              {{ overlayMessage }}
              <v-progress-linear
                indeterminate
                color="white"
                class="mb-0"
              ></v-progress-linear>
            </v-card-text>
          </v-card>
    </v-dialog>
    <div v-if="!overlay">
        <v-row justify="center" class="px-0 py-0">
            <v-col cols="12">
                <v-card color="#f5f5f5" flat tile max-width="1400" class="mx-auto">
                    <p
                        style="text-align:center;"
                        class="pt-6 display-1 font-weight-medium"
                    >
                        Cluster Administration
                    </p>
                    <p style="text-align:center;" class="body-1 font-weight-normal">
                        Copy clusters between users (Admin access required)
                    </p>

                    <!-- Error State -->
                    <v-row v-if="error">
                      <v-col cols="12">
                        <v-alert type="error" dismissible @input="error = null" class="mx-3">
                          <strong>Error:</strong> {{ error }}
                        </v-alert>
                      </v-col>
                    </v-row>

                    <!-- Main Content -->
                    <div class="mx-3 mb-6">
                      <!-- Step 1: Entity Type Selection -->
                      <v-card class="mb-6" elevation="1">
                        <v-card-title class="py-3">
                          <v-icon left color="primary">mdi-shape</v-icon>
                          Step 1: Select Cluster Type
                        </v-card-title>
                        <v-card-text class="pt-0">
                          <v-radio-group v-model="selectedEntityType" @change="onEntityTypeChange" row>
                            <v-radio
                              label="Supplier Clusters"
                              value="supplier"
                              color="primary"
                              class="mr-6"
                            ></v-radio>
                            <v-radio
                              label="Buyer Clusters"
                              value="publisher"
                              color="primary"
                            ></v-radio>
                          </v-radio-group>
                        </v-card-text>
                      </v-card>

                      <!-- Step 2: Source User Selection -->
                      <v-card class="mb-6" elevation="1" v-if="selectedEntityType">
                        <v-card-title class="py-3">
                          <v-icon left color="primary">mdi-account</v-icon>
                          Step 2: Select Source User
                        </v-card-title>
                        <v-card-text class="pt-0">
                          <v-autocomplete
                            v-model="sourceEmailDomainFilter"
                            :items="filteredSourceDomains"
                            label="Filter by Email Domain (optional)"
                            placeholder="Type to search domains..."
                            prepend-icon="mdi-email"
                            clearable
                            outlined
                            dense
                            @input="filterSourceUsers"
                            @change="filterSourceUsers"
                            @update:search-input="filterSourceDomains"
                            class="mb-3"
                          >
                            <template slot="item" slot-scope="{ item }">
                              <v-list-item-content>
                                <v-list-item-title>{{ item }}</v-list-item-title>
                              </v-list-item-content>
                            </template>
                          </v-autocomplete>

                          <v-select
                            v-model="selectedSourceUser"
                            :items="filteredSourceUsers"
                            item-text="email"
                            item-value="user_id"
                            label="Select Source User"
                            prepend-icon="mdi-account-circle"
                            :loading="loadingUsers"
                            outlined
                            dense
                            :disabled="!sourceEmailDomainFilter"
                            @change="onSourceUserChange"
                          >
                            <template slot="item" slot-scope="{ item }">
                              <v-list-item-content>
                                <v-list-item-title>{{ item.email }}</v-list-item-title>
                                <v-list-item-subtitle>{{ item.name }}</v-list-item-subtitle>
                              </v-list-item-content>
                              <v-list-item-action>
                                <v-chip small color="primary" text-color="white">
                                  {{ getTotalClusters(item) }} {{ getClusterTypeLabel() }} clusters
                                </v-chip>
                              </v-list-item-action>
                            </template>
                          </v-select>
                        </v-card-text>
                      </v-card>

                      <!-- Step 3: Cluster Selection -->
                      <v-card class="mb-6" elevation="1" v-if="selectedSourceUser && selectedEntityType">
                        <v-card-title class="py-3">
                          <v-icon left color="primary">mdi-view-grid</v-icon>
                          Step 3: Select Clusters to Copy
                        </v-card-title>
                        <v-card-text class="pt-0">
                          <v-data-table
                            v-model="selectedClusters"
                            :headers="clusterHeaders"
                            :items="sourceClusters"
                            :loading="loadingClusters"
                            item-key="id"
                            show-select
                            class="elevation-1"
                          >
                            <template slot="item.name" slot-scope="{ item }">
                              <strong>{{ item.name }}</strong>
                            </template>
                            <template slot="item.entity_count" slot-scope="{ item }">
                              <v-chip small color="info" text-color="white">
                                {{ item.entity_count }} entities
                              </v-chip>
                            </template>
                            <template slot="item.created_at" slot-scope="{ item }">
                              {{ formatDate(item.created_at) }}
                            </template>
                          </v-data-table>

                          <div class="mt-4">
                            <v-btn
                              color="primary"
                              @click="selectAllClusters"
                              :disabled="!sourceClusters.length"
                            >
                              Select All
                            </v-btn>
                            <v-btn
                              color="secondary"
                              @click="clearClusterSelection"
                              class="ml-2"
                              :disabled="!selectedClusters.length"
                            >
                              Clear Selection
                            </v-btn>
                          </div>
                        </v-card-text>
                      </v-card>

                      <!-- Step 4: Target User Selection -->
                      <v-card class="mb-6" elevation="1" v-if="selectedClusters.length > 0">
                        <v-card-title class="py-3">
                          <v-icon left color="primary">mdi-account-multiple</v-icon>
                          Step 4: Select Target Users
                        </v-card-title>
                        <v-card-text class="pt-0">
                          <v-autocomplete
                            v-model="targetEmailDomainFilter"
                            :items="filteredTargetDomains"
                            label="Filter Target Users by Email Domain (optional)"
                            placeholder="Type to search domains..."
                            prepend-icon="mdi-email"
                            clearable
                            outlined
                            dense
                            @input="filterTargetUsers"
                            @change="filterTargetUsers"
                            @update:search-input="filterTargetDomains"
                            class="mb-3"
                          >
                            <template slot="item" slot-scope="{ item }">
                              <v-list-item-content>
                                <v-list-item-title>{{ item }}</v-list-item-title>
                              </v-list-item-content>
                            </template>
                          </v-autocomplete>

                          <v-combobox
                            v-model="selectedTargetUsers"
                            :items="filteredTargetUsers"
                            item-text="email"
                            item-value="user_id"
                            label="Select Target Users"
                            prepend-icon="mdi-account-multiple"
                            multiple
                            chips
                            deletable-chips
                            outlined
                            dense
                            :disabled="!targetEmailDomainFilter"
                          >
                            <template slot="item" slot-scope="{ item }">
                              <v-list-item-content>
                                <v-list-item-title>{{ item.email }}</v-list-item-title>
                                <v-list-item-subtitle>{{ item.name }}</v-list-item-subtitle>
                              </v-list-item-content>
                            </template>
                          </v-combobox>

                          <div class="mt-4">
                            <v-alert type="info" dense>
                              <strong>Selected:</strong> {{ selectedTargetUsers.length }} target user(s)
                            </v-alert>
                          </div>
                        </v-card-text>
                      </v-card>

                      <!-- Step 5: Copy Operation -->
                      <v-card class="mb-6" elevation="1" v-if="canProceedWithCopy">
                        <v-card-title class="py-3">
                          <v-icon left color="primary">mdi-content-copy</v-icon>
                          Step 5: Copy Clusters
                        </v-card-title>
                        <v-card-text class="pt-0">
                          <v-alert type="warning" dense class="mb-4">
                            <strong>Copy Summary:</strong>
                            <ul class="mt-2">
                              <li>Source: {{ getSourceUserEmail() }}</li>
                              <li>Entity Type: {{ selectedEntityType }}</li>
                              <li>Clusters: {{ selectedClusters.length }}
                                <ul>
                                  <li v-for="cluster in selectedClusters" :key="cluster.id">{{ cluster.name }}</li>
                                </ul>
                              </li>
                              <li>Target Users: {{ selectedTargetUsers.length }}</li>
                              <li>Total Entities: {{ getTotalEntities() }}</li>
                            </ul>
                          </v-alert>

                          <div class="text-center">
                            <v-btn
                              color="primary"
                              large
                              @click="validateAndCopy"
                              :loading="copying"
                              :disabled="!canProceedWithCopy"
                            >
                              <v-icon left>mdi-content-copy</v-icon>
                              Copy Clusters
                            </v-btn>
                          </div>
                        </v-card-text>
                      </v-card>

                      <!-- Copy Results -->
                      <v-card class="mb-6" elevation="1" v-if="copyResults">
                        <v-card-title class="py-3">
                          <v-icon left :color="copyResults.status === 'success' ? 'success' : 'error'">
                            {{ copyResults.status === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                          </v-icon>
                          Copy Results
                        </v-card-title>
                        <v-card-text class="pt-0">
                          <v-alert
                            :type="copyResults.status === 'success' ? 'success' : 'error'"
                            dense
                          >
                            <strong>Status:</strong> {{ copyResults.status }}
                          </v-alert>

                          <div v-if="copyResults.status === 'success'" class="mt-4">
                            <h4>Success Summary:</h4>
                            <ul>
                              <li>Clusters Processed: {{ copyResults.data.total_clusters_processed }}</li>
                              <li>Entities Copied: {{ copyResults.data.total_entities_copied }}</li>
                              <li>Entities Skipped: {{ copyResults.data.total_entities_skipped }}</li>
                              <li>Processing Time: {{ copyResults.data.processing_time }}</li>
                            </ul>

                            <h4>Results by User:</h4>
                            <v-simple-table>
                            <template v-slot:default>
                              <thead>
                                <tr>
                                  <th>Target User</th>
                                  <th>Clusters Copied</th>
                                  <th>Entities Copied</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr v-for="(result, userId) in copyResults.data.clusters_by_user" :key="userId">
                                  <td>{{ getTargetUserEmail(userId) }}</td>
                                  <td>{{ result.clusters_copied }}</td>
                                  <td>{{ result.entities_copied }}</td>
                                </tr>
                              </tbody>
                            </template>
                            </v-simple-table>
                          </div>

                          <div v-if="copyResults.data.errors && copyResults.data.errors.length" class="mt-4">
                            <h4>Errors:</h4>
                            <v-alert type="error" dense>
                              <ul>
                                <li v-for="error in copyResults.data.errors" :key="error">{{ error }}</li>
                              </ul>
                            </v-alert>
                          </div>

                          <div class="text-center mt-4">
                            <v-btn color="primary" @click="resetForm">
                              <v-icon left>mdi-refresh</v-icon>
                              Start New Copy
                            </v-btn>
                          </div>
                        </v-card-text>
                      </v-card>
                    </div>
                </v-card>
            </v-col>
        </v-row>
    </div>
    
    <!-- Copy Results Modal -->
    <v-dialog v-model="showCopyResultsModal" max-width="800px" persistent>
      <v-card>
        <v-card-title class="headline">
          <v-icon left :color="copyResults && copyResults.status === 'success' ? 'success' : 'error'">
            {{ copyResults && copyResults.status === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
          </v-icon>
          Copy Operation Results
        </v-card-title>
        
        <v-card-text>
          <!-- Status Alert -->
          <v-alert
            :type="copyResults && copyResults.status === 'success' ? 'success' : 'error'"
            dense
            class="mb-4"
          >
            <strong>Status:</strong> {{ copyResults && copyResults.status ? copyResults.status : 'Unknown' }}
          </v-alert>

          <!-- Success Details -->
          <div v-if="copyResults && copyResults.status === 'success'" class="mb-4">
            <h4>Success Summary:</h4>
            <ul>
              <li>Clusters Processed: {{ copyResults.data.total_clusters_processed }}</li>
              <li>Entities Copied: {{ copyResults.data.total_entities_copied }}</li>
              <li>Entities Skipped: {{ copyResults.data.total_entities_skipped }}</li>
              <li>Processing Time: {{ copyResults.data.processing_time }}</li>
            </ul>

            <h4>Results by User:</h4>
            <v-simple-table class="mt-3">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Target User</th>
                    <th>Clusters Copied</th>
                    <th>Entities Copied</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(result, userId) in copyResults.data.clusters_by_user" :key="userId">
                    <td>{{ getTargetUserEmail(userId) }}</td>
                    <td>{{ result.clusters_copied }}</td>
                    <td>{{ result.entities_copied }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>

          <!-- Error Details -->
          <div v-if="copyResults && copyResults.data && copyResults.data.errors && copyResults.data.errors.length" class="mb-4">
            <h4>Errors:</h4>
            <v-alert type="error" dense>
              <ul>
                <li v-for="error in copyResults.data.errors" :key="error">{{ error }}</li>
              </ul>
            </v-alert>
          </div>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            @click="closeResultsModal"
          >
            Close
          </v-btn>
          <v-btn
            color="primary"
            @click="resetForm"
          >
            <v-icon left>mdi-refresh</v-icon>
            Start New Copy
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
</div>
</template>

<script>
import { 
  getAllUsersWithStats, 
  getUserClusters, 
  validateClusterCopy, 
  copyClusters,
  getUserDomains
} from '@/components/APIManager.js';
import { formatAdminDateTime } from "@/utils/adminDateTime.js";

export default {
  name: 'ClusterAdministration',
  data() {
    return {
      overlay: true,
      overlayMessage: 'Loading cluster administration...',
      error: null,
      loadingUsers: false,
      loadingClusters: false,
      copying: false,
      
      // User data
      allUsers: [],
      filteredSourceUsers: [],
      filteredTargetUsers: [],
      sourceEmailDomainFilter: '',
      targetEmailDomainFilter: '',
      
      // Domain data
      availableDomains: [],
      filteredSourceDomains: [],
      filteredTargetDomains: [],
      
      // Selection data
      selectedSourceUser: null,
      selectedEntityType: null,
      selectedClusters: [],
      selectedTargetUsers: [],
      
      // Cluster data
      sourceClusters: [],
      clusterHeaders: [
        { text: 'Name', value: 'name' },
        { text: 'Entity Count', value: 'entity_count' },
        { text: 'Created', value: 'created_at' }
      ],
      
      // Copy operation
      copyStatus: '',
      copyResults: null,
      showCopyResultsModal: false
    };
  },
  
  computed: {
    canProceedWithCopy() {
      return this.selectedSourceUser && 
             this.selectedEntityType && 
             this.selectedClusters.length > 0 && 
             this.selectedTargetUsers.length > 0;
    }
  },
  
  async mounted() {
    // Load users first, then domains (domains depend on users for fallback)
    await this.loadUsers();
    await this.loadDomains();
    this.overlay = false;
  },
  
  methods: {
    async loadUsers() {
      try {
        this.loadingUsers = true;
        const response = await getAllUsersWithStats();
        
        if (response.status === 'success') {
          this.allUsers = response.data.users;
          // Start with empty lists - users must select domains first
          this.filteredSourceUsers = [];
          this.filteredTargetUsers = [];
        } else {
          throw new Error('Failed to load users');
        }
      } catch (error) {
        //console.error('Error loading users:', error);
        this.error = 'Failed to load users. Please try again.';
      } finally {
        this.loadingUsers = false;
      }
    },

    async loadDomains() {
      try {
        const response = await getUserDomains();
        
        if (response.status === 'success') {
          this.availableDomains = response.data.domains;
          this.filteredSourceDomains = [...this.availableDomains];
          this.filteredTargetDomains = [...this.availableDomains];
        } else {
          throw new Error('Failed to load domains');
        }
      } catch (error) {
        //console.error('Error loading domains:', error);
        // Fallback: extract domains from user emails
        this.availableDomains = this.extractDomainsFromUsers();
        this.filteredSourceDomains = [...this.availableDomains];
        this.filteredTargetDomains = [...this.availableDomains];
      }
    },
    
    filterSourceUsers() {
      if (!this.sourceEmailDomainFilter) {
        this.filteredSourceUsers = [];
      } else {
        const domain = this.sourceEmailDomainFilter.toLowerCase();
        this.filteredSourceUsers = this.allUsers.filter(user => {
          if (!user.email) return false;
          const emailDomain = user.email.toLowerCase().split('@')[1];
          return emailDomain && (emailDomain === domain || emailDomain.includes('.' + domain));
        });
        
        // Clear source user selection when changing domains
        this.selectedSourceUser = null;
      }
    },

    filterTargetUsers() {
      if (!this.targetEmailDomainFilter) {
        this.filteredTargetUsers = [];
      } else {
        const domain = this.targetEmailDomainFilter.toLowerCase();
        this.filteredTargetUsers = this.allUsers.filter(user => {
          if (!user.email) return false;
          const emailDomain = user.email.toLowerCase().split('@')[1];
          return emailDomain && (emailDomain === domain || emailDomain.includes('.' + domain));
        });
        // Clear target users selection when changing domains
        this.selectedTargetUsers = [];
      }
    },

    filterSourceDomains(searchTerm) {
      if (!searchTerm) {
        this.filteredSourceDomains = [...this.availableDomains];
      } else {
        const term = searchTerm.toLowerCase();
        this.filteredSourceDomains = this.availableDomains.filter(domain => 
          domain.toLowerCase().includes(term)
        );
      }
    },

    filterTargetDomains(searchTerm) {
      if (!searchTerm) {
        this.filteredTargetDomains = [...this.availableDomains];
      } else {
        const term = searchTerm.toLowerCase();
        this.filteredTargetDomains = this.availableDomains.filter(domain => 
          domain.toLowerCase().includes(term)
        );
      }
    },

    // Fallback method to extract domains from user emails if API fails
    extractDomainsFromUsers() {
      const domains = new Set();
      this.allUsers.forEach(user => {
        if (user.email && user.email.includes('@')) {
          const domain = user.email.split('@')[1];
          if (domain && domain.includes('.')) {
            domains.add(domain);
          }
        }
      });
      return Array.from(domains).sort();
    },
    
    async onSourceUserChange() {
      if (this.selectedSourceUser && this.selectedEntityType) {
        await this.loadSourceClusters();
      }
    },
    
    async onEntityTypeChange() {
      if (this.selectedSourceUser && this.selectedEntityType) {
        await this.loadSourceClusters();
      }
    },
    
    async loadSourceClusters() {
      try {
        this.loadingClusters = true;
        const response = await getUserClusters(this.selectedSourceUser, this.selectedEntityType);
        
        if (response.status === 'success') {
          this.sourceClusters = response.data.clusters;
          this.selectedClusters = []; // Clear previous selection
        } else {
          throw new Error('Failed to load clusters');
        }
      } catch (error) {
        //console.error('Error loading clusters:', error);
        this.error = 'Failed to load clusters. Please try again.';
      } finally {
        this.loadingClusters = false;
      }
    },
    
    selectAllClusters() {
      this.selectedClusters = [...this.sourceClusters];
    },
    
    clearClusterSelection() {
      this.selectedClusters = [];
    },
    
    async validateAndCopy() {
      try {
        this.copying = true;
        this.overlay = true;
        this.overlayMessage = 'Validating copy operation...';
        
        // Prepare copy data
        const copyData = {
          source_user_id: this.selectedSourceUser,
          target_user_ids: this.selectedTargetUsers.map(user => 
            typeof user === 'string' ? user : user.user_id
          ),
          entity_type: this.selectedEntityType,
          cluster_ids: this.selectedClusters.map(cluster => cluster.id)
        };
        
        // Validate first
        this.overlayMessage = 'Validating operation...';
        const validation = await validateClusterCopy(copyData);
        
        if (!validation.data.valid) {
          throw new Error('Validation failed');
        }
        
        // Execute copy
        this.overlayMessage = 'Copying clusters...';
        const result = await copyClusters(copyData);
        
        this.copyResults = result;
        this.overlay = false;
        this.showCopyResultsModal = true;
        
      } catch (error) {
        //console.error('Error during copy operation:', error);
        this.error = 'Copy operation failed. Please try again.';
        this.copyResults = {
          status: 'error',
          data: {
            errors: [error.message || 'Unknown error occurred']
          }
        };
        this.overlay = false;
        this.showCopyResultsModal = true;
      } finally {
        this.copying = false;
      }
    },
    
    closeResultsModal() {
      this.showCopyResultsModal = false;
    },
    
    resetForm() {
      this.selectedSourceUser = null;
      this.selectedEntityType = null;
      this.selectedClusters = [];
      this.selectedTargetUsers = [];
      this.sourceClusters = [];
      this.copyResults = null;
      this.error = null;
      this.showCopyResultsModal = false;
      this.sourceEmailDomainFilter = '';
      this.targetEmailDomainFilter = '';
      this.filteredSourceUsers = [];
      this.filteredTargetUsers = [];
      this.filteredSourceDomains = [...this.availableDomains];
      this.filteredTargetDomains = [...this.availableDomains];
    },
    
    getTotalClusters(user) {
      if (!user.cluster_stats) return 0;
      
      // If no entity type is selected, show total clusters
      if (!this.selectedEntityType) {
        return user.cluster_stats.total_clusters || 0;
      }
      
      // Show clusters based on selected entity type
      if (this.selectedEntityType === 'supplier') {
        return user.cluster_stats.supplier_clusters || 0;
      } else if (this.selectedEntityType === 'publisher') {
        return user.cluster_stats.publisher_clusters || 0;
      }
      
      return user.cluster_stats.total_clusters || 0;
    },
    
    getClusterTypeLabel() {
      if (!this.selectedEntityType) return '';
      return this.selectedEntityType === 'supplier' ? 'supplier' : 'buyer';
    },
    
    getSourceUserEmail() {
      const user = this.allUsers.find(u => u.user_id === this.selectedSourceUser);
      return user ? user.email : 'Unknown';
    },
    
    getTargetUserEmail(userId) {
      // Handle both string user_id and object user
      const actualUserId = typeof userId === 'string' ? userId : userId.user_id;
      const user = this.allUsers.find(u => u.user_id === actualUserId);
      return user ? user.email : 'Unknown';
    },
    
    getTotalEntities() {
      return this.selectedClusters.reduce((total, cluster) => total + cluster.entity_count, 0);
    },
    
    formatDate(dateString) {
      return formatAdminDateTime(dateString, "N/A");
    }
  }
};
</script>

<style scoped>
.truncate {
    font-family:sans-serif;
    font-size: 1em;
    font-weight: bold;
    line-height: 1.2em;
    transition: var(--transition);
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden ;
    text-overflow: ellipsis;
}

.txt {
    font-weight: normal;
    line-height: 1.2;
    letter-spacing: -0.02em;
    color: var(--dark-grey);
}

.truncate-title {
    line-height: 2em;
    transition: var(--transition);
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden ;
    text-overflow: ellipsis;
}

#no-background-hover::before {
   background-color: transparent !important;
}
</style>
